/**
 * Shared strings (shell, generic actions, time). Swedish is the source of
 * truth — `en/common.ts` is typed against this object, so a key added here
 * without an English counterpart fails `pnpm typecheck`.
 */
const common = {
  app: { name: 'HUB' },
  nav: {
    calendar: 'Kalender',
    draw: 'Whiteboard',
    write: 'Dokument',
    tasks: 'Uppgifter',
    inbox: 'Inkorg',
    chat: 'Chat',
    settings: 'Inställningar',
    profile: 'Profilinställningar',
    meetings: 'Möte',
  },
  userMenu: {
    profileSettings: 'Profilinställningar',
    superAdmin: 'Super Admin',
    theme: 'Tema',
    light: 'Ljust',
    dark: 'Mörkt',
    system: 'System',
    language: 'Språk',
    logout: 'Logga ut',
    anonymous: 'Användare',
    loggingOut: 'Loggar ut...',
  },
  voice: {
    female: 'Kvinnlig',
    male: 'Manlig',
  },
  language: {
    sv: 'Svenska',
    en: 'English',
  },
  mobileNav: {
    openMenu: 'Öppna menyn',
    menu: 'Meny',
    description: 'Navigering och chattar',
  },
  navbar: {
    connecting: 'Ansluter…',
    disconnected: 'Frånkopplad',
    renameTitle: 'Byt namn',
  },
  actions: {
    save: 'Spara',
    cancel: 'Avbryt',
    close: 'Stäng',
    delete: 'Ta bort',
    edit: 'Redigera',
    rename: 'Byt namn',
    retry: 'Försök igen',
    copy: 'Kopiera',
    copied: 'Kopierat',
    search: 'Sök',
    open: 'Öppna',
    create: 'Skapa',
    add: 'Lägg till',
    remove: 'Ta bort',
    back: 'Tillbaka',
    continue: 'Fortsätt',
    confirm: 'Bekräfta',
    download: 'Ladda ner',
    upload: 'Ladda upp',
    send: 'Skicka',
    loading: 'Laddar…',
    saving: 'Sparar…',
  },
  time: {
    today: 'Idag',
    yesterday: 'Igår',
    justNow: 'Nyss',
  },
  errors: {
    generic: 'Något gick fel',
    network: 'Nätverksfel',
    notFound: 'Hittades inte',
    unauthorized: 'Du måste vara inloggad',
    forbidden: 'Du saknar behörighet',
  },
  errorPage: {
    title: 'Den här sidan kunde inte visas',
    body: 'Något gick fel när sidan skulle visas. Prova igen, eller gå till projektets startsida.',
    staleBody:
      'HUB har uppdaterats sedan du öppnade fliken, så en del av sidan saknas. Ladda om för att få den nya versionen.',
    persistHint:
      'Händer det igen? Ladda om sidan och berätta för oss vad du gjorde, gärna med fel-id:t nedan.',
    tryAgain: 'Försök igen',
    reload: 'Ladda om sidan',
    goToProject: 'Till projektets startsida',
    goHome: 'Till startsidan',
    technicalDetails: 'Tekniska detaljer',
    errorId: 'Fel-id',
    route: 'Sida',
    version: 'Version',
    componentStack: 'Komponenter',
  },
  newVersion: {
    title: 'Ny version av HUB finns',
    description: 'Ladda om sidan för att få de senaste ändringarna.',
    reload: 'Ladda om',
  },
  presence: {
    online: 'Online',
    goTo: 'Gå till',
  },
  sidebar: {
    expand: 'Expandera sidofält',
    collapse: 'Dölj sidofält',
    projectFallback: 'Projekt',
    projects: 'Projekt',
    newProject: 'Nytt projekt',
    searchProjects: 'Sök projekt…',
    noProjectsMatch: 'Inga projekt matchar',
    chats: 'Chattar',
    newChat: 'Ny chatt',
    lastMessage: 'Senaste meddelande {{when}}',
    noChats: 'Inga chattar ännu',
    agentWorking: 'Agenten arbetar',
    pinned: 'Fästa',
    recent: 'Senaste',
    allChats: 'Alla chattar',
    pin: 'Fäst',
    unpin: 'Avfäst',
    renameChatTitle: 'Byt namn på chatt',
    renameChatDescription: 'Ange ett nytt namn för chatten.',
    chatNamePlaceholder: 'Chattnamn',
    deleteChatTitle: 'Ta bort chatt',
    deleteChatConfirm: 'Är du säker på att du vill ta bort "{{title}}"? Detta går inte att ångra.',
    deleting: 'Tar bort...',
  },
  home: {
    noProjectsTitle: 'Inga projekt ännu',
    noProjectsBody:
      'Du har inga projekt ännu. Be en administratör att bjuda in dig till ett projekt.',
    goToAdmin: 'Gå till Admin',
  },
  project: {
    loading: 'Laddar projekt...',
    notFoundTitle: 'Projektet hittades inte',
    notFoundBody: 'Projektet kunde inte laddas eller så saknar du behörighet.',
    goHome: 'Gå till startsidan',
  },
  createProject: {
    title: 'Nytt projekt',
    count: '{{count}}/{{limit}} projekt',
    limitReached:
      'Du har nått gränsen på {{limit}} projekt. Ta bort ett projekt för att kunna skapa ett nytt.',
    organization: 'Organisation',
    name: 'Projektnamn',
    namePlaceholder: 'Mitt projekt',
    icon: 'Ikon',
    creating: 'Skapar...',
    submit: 'Skapa projekt',
    failed: 'Kunde inte skapa projektet.',
  },
  credits: {
    label: 'Användning',
    percent: '{{percent}} %',
    orgLine: '{{used}} av {{limit}} krediter använda',
    projectLine: 'Detta projekt: {{used}} av {{cap}} krediter',
    periodEnd: 'Perioden återställs {{date}}',
    blocked: 'Krediterna är slut – nya svar, uppläsning och diktering är pausade.',
  },
  projectList: {
    recentProjects: 'Senaste Projekt',
    members_one: '{{count}} medlem',
    members_other: '{{count}} medlemmar',
    hoursAgo_one: '{{count}} timme sedan',
    hoursAgo_other: '{{count}} timmar sedan',
    daysAgo_one: '{{count}} dag sedan',
    daysAgo_other: '{{count}} dagar sedan',
    status: {
      inProgress: 'Pågående',
      planning: 'Planering',
      review: 'Granskning',
    },
  },
}

export default common
